import React, { useMemo } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import { useNavigate } from 'react-router-dom';
import { TextFieldWrapper } from 'components/Input/TextFieldWrapper';
import { InputPasswordWrapper } from 'components/Input/InputPassword/InputPasswordWrapper';
import { LOGIN_FORM_LABELS, LOGIN_FORM_NAMES } from 'shared/constants/form-constants';
import { FORMS_BUTTON } from 'shared/constants/buttons-constants';
import { urls } from 'shared/constants/routing.constants';
import { useAppDispatch } from 'redux/hooks/reduxHooks';
import { TokenRequest, TError } from 'shared/types/api.interface';
import { openCurrentModal, closeCurrentModal } from 'redux/slices/modals/modals.slice';
import { EModalsNames } from 'shared/types/modal.interface';
import { useLoginMutation } from 'redux/api/auth.api';
import { useLoading } from 'shared/hooks/useLoading';
import { LOCAL_STORAGE } from 'shared/constants/local-storage-constants';
import { TLoginFormType, LoginFormValidation } from './vatidationSchema';

const fieldsArr = [LOGIN_FORM_NAMES.email, LOGIN_FORM_NAMES.password];

export const LoginForm = () => {
  const {
    handleSubmit,
    reset,
    control,
    setError,
    formState: { errors },
  } = useForm<TLoginFormType>({
    defaultValues: {
      [LOGIN_FORM_NAMES.email]: '',
      [LOGIN_FORM_NAMES.password]: '',
    },
    resolver: zodResolver(LoginFormValidation),
  });
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [login, { isLoading }] = useLoginMutation();
  useLoading(isLoading);

  const catchSubmitError = (errorsArr: TError[]) => {
    fieldsArr?.forEach((field) => {
      setError(field, {
        type: 'custom',
        message: errorsArr.find((item: TError) => item.attr === field)?.detail || '',
      });
    });
  };

  const nextUrl = useMemo(() => {
    const challengeId = localStorage.getItem(LOCAL_STORAGE.currentChallengeId);
    const toGenerateWithAI = localStorage.getItem(LOCAL_STORAGE.toGenerateWithAI);

    if (toGenerateWithAI) return urls.commonPages.generateTopics;
    if (challengeId) return `${urls.commonPages.challengeCard}/${challengeId}`;
    return urls.myPages.profile;
  }, []);

  const onSubmit: SubmitHandler<TLoginFormType> = async (data: TokenRequest) => {
    try {
      await login(data).unwrap();
      reset();
      dispatch(closeCurrentModal());
      setTimeout(() => navigate(nextUrl), 500);
    } catch (err) {
      // оставила тут отлавливание, тк почему-то, несмотря на типизацию error,
      // внутри try catch на ts не ругается на наличие data, а в useEffect на error из хука - ругается
      catchSubmitError(err?.data?.errors);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack alignItems="stretch" width="100%">
        <Stack sx={{ rowGap: { lg: '37px', md: '40px', sm: '40px', xs: '40px' } }}>
          <TextFieldWrapper
            name={LOGIN_FORM_NAMES.email}
            control={control}
            label={LOGIN_FORM_LABELS.email}
            errorMessage={errors.email ? (errors.email?.message as string) : ''}
          />
          <InputPasswordWrapper
            name={LOGIN_FORM_NAMES.password}
            control={control}
            label={LOGIN_FORM_LABELS.password}
            errorMessage={errors.password ? (errors.password?.message as string) : ''}
          />
        </Stack>
        <Button
          variant="link"
          sx={{ mt: '8px', alignSelf: 'flex-end', position: 'relative' }}
          onClick={() => {
            dispatch(
              openCurrentModal({ popupName: EModalsNames.RestorePasswordModal, data: { type: 0 } }),
            );
          }}
        >
          {FORMS_BUTTON.forgotPasswordButton}
        </Button>
        <Button
          sx={{
            mt: { lg: '64px', md: '24px', sm: '24px', xs: '24px' },
            alignSelf: 'center',
          }}
          variant="fixedSize"
          type="submit"
        >
          {FORMS_BUTTON.loginSubmitButton}
        </Button>
        {/* <Stack sx={{ mt: '20px', alignSelf: 'center' }}>
          <ReCAPTCHA onChange={handleCaptchaChange} sitekey={process.env.REACT_APP_SITE_KEY} />
        </Stack> */}
      </Stack>
    </form>
  );
};
